import { c as _c } from "react/compiler-runtime";
import { SvgCheck } from '@common/icon';
import { cn } from '@core/utils';
import { useEffect, useRef, useState } from 'react';
import { useGetter } from '@/hooks/useGetter';
import { MODAL_CLOSING_STATE, useModalClosing } from '@/hooks/useModalClosing';
import type { HTMLTagProps } from '@/types';
export type ValidOptionValue = string | number;
export interface SelectChangeEvent<TOptionValue extends ValidOptionValue, TMultiple extends boolean, TCancelable extends boolean> {
  value: TMultiple extends true ? TOptionValue[] : TCancelable extends true ? TOptionValue | undefined : TOptionValue;
}
export type OptionProps<TOptionValue extends ValidOptionValue, TMultiple extends boolean = false, TCancelable extends boolean = false> = Omit<HTMLTagProps<'section'>, 'value' | 'onChange' | 'onKeyDown'> & {
  opened?: boolean;
  options?: {
    label: string;
    value: TOptionValue;
  }[];
  multiple?: TMultiple;
  cancelable?: TCancelable;
  value?: TMultiple extends true ? TOptionValue[] : TOptionValue | undefined;
  onChange?: (event: SelectChangeEvent<TOptionValue, TMultiple, TCancelable>) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  float?: 'top' | 'bottom';
};
const INITIAL_INDEX = -1;
export const Option = t0 => {
  const $ = _c(31);
  let className;
  let onChange;
  let onKeyDown;
  let options;
  let restSectionProps;
  let selectedValue;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      opened: t1,
      options,
      multiple: t2,
      cancelable: t3,
      value: selectedValue,
      onChange,
      onKeyDown,
      float: t4,
      className,
      ...restSectionProps
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onChange;
    $[3] = onKeyDown;
    $[4] = options;
    $[5] = restSectionProps;
    $[6] = selectedValue;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    className = $[1];
    onChange = $[2];
    onKeyDown = $[3];
    options = $[4];
    restSectionProps = $[5];
    selectedValue = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  const opened = t1 === undefined ? false : t1;
  const multiple = t2 === undefined ? false as TMultiple : t2;
  const cancelable = t3 === undefined ? false as TCancelable : t3;
  const float = t4 === undefined ? "bottom" : t4;
  let t5;
  if ($[11] !== opened) {
    t5 = {
      opened,
      closingDuration: 100
    };
    $[11] = opened;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const closingTransition = useModalClosing(t5);
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const optionItemRefs = useRef(t6);
  const indexForSelectState = useState(INITIAL_INDEX);
  const [indexForSelect, setIndexForSelect] = indexForSelectState;
  const getIndexForSelect = useGetter(indexForSelect);
  let t7;
  let t8;
  if ($[14] !== closingTransition || $[15] !== getIndexForSelect || $[16] !== onKeyDown || $[17] !== options) {
    t7 = () => {
      if (closingTransition === MODAL_CLOSING_STATE.OPENED && options) {
        const prevFocusedElement = document.activeElement;
        const focusOptionItem = indexForSelect_0 => {
          optionItemRefs.current[indexForSelect_0]?.focus();
          setIndexForSelect(indexForSelect_0);
        };
        const keyboardEvent = event => {
          onKeyDown?.(event);
          let indexForSelect_1 = getIndexForSelect();
          bb33: switch (event.key) {
            case "ArrowUp":
              {
                event.preventDefault();
                if (indexForSelect_1 > 0) {
                  indexForSelect_1 = indexForSelect_1 - 1;
                  focusOptionItem(indexForSelect_1);
                }
                break bb33;
              }
            case "ArrowDown":
              {
                event.preventDefault();
                if (indexForSelect_1 < options.length - 1) {
                  indexForSelect_1 = indexForSelect_1 + 1;
                  focusOptionItem(indexForSelect_1);
                }
                break bb33;
              }
            case "Enter":
              {
                event.preventDefault();
                if (indexForSelect_1 !== INITIAL_INDEX) {
                  optionItemRefs.current[indexForSelect_1]?.click();
                }
                break bb33;
              }
            default:
              {
                if (prevFocusedElement instanceof HTMLElement) {
                  prevFocusedElement?.focus();
                }
              }
          }
        };
        document.addEventListener("keydown", keyboardEvent);
        return () => document.removeEventListener("keydown", keyboardEvent);
      }
    };
    t8 = [getIndexForSelect, onKeyDown, closingTransition, options, optionItemRefs, setIndexForSelect];
    $[14] = closingTransition;
    $[15] = getIndexForSelect;
    $[16] = onKeyDown;
    $[17] = options;
    $[18] = t7;
    $[19] = t8;
  } else {
    t7 = $[18];
    t8 = $[19];
  }
  useEffect(t7, t8);
  let t9;
  if ($[20] !== cancelable || $[21] !== className || $[22] !== closingTransition || $[23] !== float || $[24] !== indexForSelect || $[25] !== multiple || $[26] !== onChange || $[27] !== options || $[28] !== restSectionProps || $[29] !== selectedValue) {
    t9 = closingTransition && options?.length ? <section {...restSectionProps} className={cn("animate-fade-in absolute z-[1] box-border w-full cursor-default rounded-xsmall border border-Gray-400 bg-Gray-white py-5 text-Gray-900 text-[inherit] animate-duration-[200ms]", float === "top" && "bottom-full mb-5", float === "bottom" && "top-full mt-5", closingTransition === MODAL_CLOSING_STATE.CLOSING && "animate-fade-out animate-duration-[100ms]", className)}><ul className="m-0 flex max-h-[30dvh] snap-y snap-mandatory flex-col overflow-y-scroll p-0 hide-scrollbar">{options?.map((t10, index) => {
          const {
            label,
            value
          } = t10;
          const isHovered = index === indexForSelect;
          const isSelected = (() => {
            if (selectedValue === undefined) {
              return false;
            }
            if (multiple && Array.isArray(selectedValue)) {
              return (selectedValue as TOptionValue[]).includes(value);
            }
            return selectedValue === value;
          })();
          return <li key={index} className="w-full snap-start"><button ref={element => {
              optionItemRefs.current[index] = element;
            }} autoFocus={isSelected} className={cn("font-normal box-border flex min-w-full max-w-full cursor-pointer snap-start items-center gap-[1em] whitespace-pre-wrap border-none bg-transparent p-12 text-start font-[inherit] text-[inherit] text-inherit outline-none", isHovered && "bg-[#f5f6ff]", isSelected && "font-medium text-B-500")} type="button" onClick={() => {
              if (multiple) {
                let valuesForSelect = selectedValue as TOptionValue[] ?? [];
                const handleChange = onChange as MultipleSelectProps['onChange'];
                if (cancelable) {
                  valuesForSelect = isSelected ? valuesForSelect?.filter(selectedValue_0 => selectedValue_0 !== value) : [...valuesForSelect, value];
                }
                handleChange?.({
                  value: valuesForSelect
                });
              } else {
                const handleChange_0 = onChange as SingleSelectProps['onChange'];
                handleChange_0?.({
                  value: isSelected && cancelable ? undefined : value
                } as SelectChangeEvent<TOptionValue, false, TCancelable>);
              }
            }} onMouseEnter={() => setIndexForSelect(index)}><div>{label}</div><div className={cn("m-[0.2em] ml-auto", isSelected ? "visible" : "invisible")}><SvgCheck className="size-[1.2em]" /></div></button></li>;
        })}</ul></section> : <></>;
    $[20] = cancelable;
    $[21] = className;
    $[22] = closingTransition;
    $[23] = float;
    $[24] = indexForSelect;
    $[25] = multiple;
    $[26] = onChange;
    $[27] = options;
    $[28] = restSectionProps;
    $[29] = selectedValue;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
};